import axios from 'axios';

// In production, requests go through the Netlify serverless proxy (no CORS issues,
// token stays server-side). In development, call Monday directly using the .env token.
const IS_PROD = process.env.NODE_ENV === 'production';
const MONDAY_API_URL = IS_PROD
  ? '/api/monday-proxy'
  : 'https://api.monday.com/v2';

const API_TOKEN = process.env.REACT_APP_MONDAY_API_TOKEN;

// Single helper so every API call goes through the same routing logic.
async function mondayPost(body) {
  const headers = IS_PROD
    ? { 'Content-Type': 'application/json' }                          // proxy adds auth
    : { Authorization: API_TOKEN, 'Content-Type': 'application/json' }; // dev: direct
  return axios.post(MONDAY_API_URL, body, { headers });
}
export const PROSPECTING_BOARD_ID  = '5173290607';
export const CONTACTS_BOARD_ID     = '5173290608';
export const ACTIVITY_LOG_BOARD_ID = '18408658779';
export const OPPS_BOARD_ID         = '9025069075';

// ── Opps board column IDs ──────────────────────────────────────────────────
const OPPS_COLS = {
  tmContact:       'multiple_person_mksabp4m', // TM / Initial Contact (people, multi)
  advisor:         'person',                    // Advisor (people, single)
  contacts:        'connect_boards_Mjj643uy',   // Link to Contacts board (board_relation)
  company:         'lookup_mkqs58br',           // Company (mirror from Contacts)
  phone:           'lookup_mkqq91r9',           // Phone (mirror from Contacts)
  email:           'lookup_mkqqjktp',           // Email (mirror from Contacts)
  pipelineStage:   'status',                    // Pipeline Stage
  source:          'dropdown_mksa3qbw',         // Source / Lead channel (dropdown)
  propertyAddress: 'lookup_mkszkmk0',           // Property Address (mirror from Of Interest)
  listingLink:     'lookup_mkt3gex5',           // Listing Link (mirror from Of Interest)
  emailContent:    'long_text_mkt3h67e',        // Email Content (long text)
  dealDate:        'date1',                     // Deal creation date
};

/** Builds a direct Monday.com item URL */
export function mondayItemUrl(boardId, itemId) {
  return `https://sterlinghq.monday.com/boards/${boardId}/pulses/${itemId}`;
}

// Prospect types that belong in the active queue
const INCLUDED_PROSPECT_TYPES = [
  'int requirement',
  'ext requirement',
  'off market / pocket',
  'off market/pocket',
];

// Keyword-based priority weight so format variations don't break sorting
function getPriorityWeight(priorityText) {
  const p = (priorityText || '').toLowerCase();
  if (p.includes('urgent'))    return 1;
  if (p.includes('motivated')) return 2;
  if (p.includes('active'))    return 3;
  return 99;
}

function getColText(item, colId) {
  const col = item.column_values?.find((c) => c.id === colId);
  return col?.text || '';
}


/**
 * Returns today's date as a plain YYYY-MM-DD string (local time).
 */
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Updates the Pipeline Stage on the Opps board.
 * stage should be 'Discarded' or 'LOST' (exact Monday label).
 */
export async function updateOppsPipelineStage(oppsItemId, stage) {
  const mutation = `
    mutation {
      change_multiple_column_values(
        board_id: ${OPPS_BOARD_ID},
        item_id: ${oppsItemId},
        column_values: ${JSON.stringify(JSON.stringify({ status: { label: stage } }))}
      ) { id }
    }
  `;
  const response = await mondayPost({ query: mutation });
  if (response.data?.errors?.length) {
    throw new Error(response.data.errors.map((e) => e.message).join('; '));
  }
}

/**
 * Discards or marks Lost an Opps lead.
 * Logs to Activity Log and updates the Pipeline Stage.
 * stage: 'Discarded' | 'LOST'
 */
export async function discardOppsLead(oppsItemId, contactItemId, reason, stage) {
  const today     = todayStr();
  const label     = stage === 'LOST' ? 'Lost' : 'Discard';
  const itemName  = `${label} — ${reason} — ${today}`;

  const columnValues = {
    date4:             { date: today },
    dropdown_mm2dv7tw: { labels: ['Discard'] },
    dropdown_mm2d1dzc: { labels: [reason] },
  };
  if (contactItemId) {
    columnValues.board_relation_mm2dp4tg = { item_ids: [Number(contactItemId)] };
  }

  const createMutation = `
    mutation {
      create_item(
        board_id: ${ACTIVITY_LOG_BOARD_ID},
        item_name: ${JSON.stringify(itemName)},
        column_values: ${JSON.stringify(JSON.stringify(columnValues))}
      ) { id }
    }
  `;

  await mondayPost({ query: createMutation });
  await updateOppsPipelineStage(oppsItemId, stage);
}

/**
 * Fetches and structures the prospecting queue for a given broker.
 * Returns { overdue, dueToday, comingUp } arrays, each sorted per spec.
 */
export async function fetchProspectingQueue(brokerName = 'Matt Mellott') {
  // NOTE: query_params with any filter breaks board_relation linked_items resolution
  // in the Monday API. We fetch without server-side filtering and apply all filters
  // client-side (same approach used for the Opps board).
  // We paginate through all items using Monday's cursor-based pagination.
  const cutoff = addDays(todayStr(), 3);

  const ITEM_FIELDS = `
    id
    name
    column_values {
      id type text value
      ... on BoardRelationValue { linked_items { id } }
    }
  `;

  // Main P.O.C. column ID is 'person' on the Prospecting board (verified).
  // Hardcoded to avoid an extra `columns { id title }` round-trip on every load.
  const mainPocColId = 'person';

  const firstPageQuery = `
    query {
      boards(ids: [${PROSPECTING_BOARD_ID}]) {
        items_page(limit: 250) {
          cursor
          items { ${ITEM_FIELDS} }
        }
      }
    }
  `;

  const firstRes = await mondayPost({ query: firstPageQuery });
  if (firstRes.data?.errors?.length) {
    throw new Error(firstRes.data.errors.map((e) => e.message).join('; '));
  }

  const board = firstRes.data?.data?.boards?.[0];
  if (!board) throw new Error('No board data returned from Monday.com');

  let allItems = [...(board.items_page?.items ?? [])];
  let cursor   = board.items_page?.cursor ?? null;

  // Fetch remaining pages
  while (cursor) {
    const nextRes = await mondayPost({
      query: `
        query {
          next_items_page(limit: 250, cursor: ${JSON.stringify(cursor)}) {
            cursor
            items { ${ITEM_FIELDS} }
          }
        }
      `,
    });
    if (nextRes.data?.errors?.length) break; // non-fatal: use what we have
    const page = nextRes.data?.data?.next_items_page;
    if (!page?.items?.length) break;
    allItems = [...allItems, ...page.items];
    cursor = page.cursor ?? null;
  }
  const today = todayStr();

  const filtered = allItems.filter((item) => {
    // 1. Main P.O.C. must match the broker
    const pocText = getColText(item, mainPocColId).toLowerCase();
    if (!pocText.includes(brokerName.toLowerCase())) return false;

    // 2. Prospect Type must be in the included list
    const prospectType = getColText(item, 'status1').toLowerCase().trim();
    if (!INCLUDED_PROSPECT_TYPES.some((t) => prospectType.includes(t))) return false;

    // 3. Priority must be Urgent, Motivated, or Active
    const priority = getColText(item, 'priority_1').toLowerCase().trim();
    if (!priority.includes('urgent') && !priority.includes('motivated') && !priority.includes('active')) return false;

    // 4. Must have a Next Follow Up date on or before today + 3 days
    const followUpDate = getColText(item, 'date').trim();
    if (!followUpDate || followUpDate > cutoff) return false;

    return true;
  });

  // Bucket and sort
  const overdue = [];
  const dueToday = [];
  const comingUp = [];

  filtered.forEach((item) => {
    const followUp = getColText(item, 'date').trim();
    if (followUp < today) overdue.push(item);
    else if (followUp === today) dueToday.push(item);
    else comingUp.push(item);
  });

  // Overdue: oldest date first, then by priority within same date
  overdue.sort((a, b) => {
    const da = getColText(a, 'date');
    const db = getColText(b, 'date');
    if (da !== db) return da < db ? -1 : 1;
    return getPriorityWeight(getColText(a, 'priority_1')) - getPriorityWeight(getColText(b, 'priority_1'));
  });

  // Due today: priority order, then alphabetical
  dueToday.sort((a, b) => {
    const pa = getPriorityWeight(getColText(a, 'priority_1'));
    const pb = getPriorityWeight(getColText(b, 'priority_1'));
    if (pa !== pb) return pa - pb;
    return a.name.localeCompare(b.name);
  });

  // Coming up: soonest date first
  comingUp.sort((a, b) => {
    const da = getColText(a, 'date');
    const db = getColText(b, 'date');
    return da < db ? -1 : da > db ? 1 : 0;
  });

  // Tag each item with the resolved column ID so LogActivity can read the
  // broker name directly from the item without extra prop-drilling.
  const tag = (item) => ({ ...item, _mainPocColId: mainPocColId });
  return {
    overdue:      overdue.map(tag),
    dueToday:     dueToday.map(tag),
    comingUp:     comingUp.map(tag),
    mainPocColId,
  };
}

/**
 * Returns the broker name from the Main P.O.C. column of a prospecting item.
 * mainPocColId comes from fetchProspectingQueue's return value.
 * Falls back to the generic 'person' column if not provided.
 */
export function getBrokerFromProspect(prospectingItem, mainPocColId = 'person') {
  return getColText(prospectingItem, mainPocColId) || '';
}

/**
 * Extracts the linked Contacts item ID from a Prospecting queue item.
 * The connect_boards0 column value is JSON: { linkedPulseIds: [{ linkedPulseId: 123 }] }
 */
export function getLinkedContactId(prospectingItem) {
  const relationCols = prospectingItem.column_values?.filter((c) => c.type === 'board_relation') ?? [];

  // Use linked_items (reliable) first, fall back to parsing value JSON
  for (const col of relationCols) {
    const idFromLinkedItems = col.linked_items?.[0]?.id;
    if (idFromLinkedItems) return idFromLinkedItems;
    if (col.value) {
      try {
        const parsed = JSON.parse(col.value);
        const id = parsed?.linkedPulseIds?.[0]?.linkedPulseId;
        if (id) return String(id);
      } catch { continue; }
    }
  }
  return null;
}

/**
 * Extracts the linked Contacts item ID from an Opps board item.
 * Uses the connect_boards_Mjj643uy column (board_relation).
 */
export function getOppsContactId(oppsItem) {
  const col = oppsItem.column_values?.find((c) => c.id === OPPS_COLS.contacts);
  if (!col) return null;
  if (col.linked_items?.length) return String(col.linked_items[0].id);
  if (col.value) {
    try {
      const parsed = JSON.parse(col.value);
      const id = parsed?.linkedPulseIds?.[0]?.linkedPulseId;
      if (id) return String(id);
    } catch { /* fall through */ }
  }
  return null;
}

// Opps pipeline stages — display config
export const OPPS_STAGE_CONFIG = {
  'Contact Added':          { label: 'Contact Added',   bg: '#f3f4f6', color: '#6b7280',  group: 'new' },
  'Email 1':                { label: 'Email 1',         bg: '#EEEDFE', color: '#3C3489',  group: 'sequence' },
  'Phone Call 1':           { label: 'Call 1',          bg: '#E1F5EE', color: '#085041',  group: 'sequence' },
  'Email 2':                { label: 'Email 2',         bg: '#EEEDFE', color: '#3C3489',  group: 'sequence' },
  'Phone Call 2':           { label: 'Call 2',          bg: '#E1F5EE', color: '#085041',  group: 'sequence' },
  'Email 3':                { label: 'Email 3',         bg: '#EEEDFE', color: '#3C3489',  group: 'sequence' },
  'Phone Call 3':           { label: 'Call 3',          bg: '#E1F5EE', color: '#085041',  group: 'sequence' },
  'Email 4':                { label: 'Email 4',         bg: '#EEEDFE', color: '#3C3489',  group: 'sequence' },
  'Phone Call 4':           { label: 'Call 4',          bg: '#E1F5EE', color: '#085041',  group: 'sequence' },
  'Email 5':                { label: 'Email 5',         bg: '#EEEDFE', color: '#3C3489',  group: 'sequence' },
  'Phone Call 5':           { label: 'Call 5',          bg: '#E1F5EE', color: '#085041',  group: 'sequence' },
  'Connection Established': { label: 'Connected',       bg: '#D1FAE5', color: '#065F46',  group: 'connected' },
  'Opportunity Won':        { label: 'Won',             bg: '#1D9E75', color: '#FFFFFF',  group: 'closed' },
  'LOST':                   { label: 'Lost',            bg: '#fee2e2', color: '#7f1d1d',  group: 'closed' },
  'Discarded':              { label: 'Discarded',       bg: '#f3f4f6', color: '#9ca3af',  group: 'discarded' },
};

/**
 * Fetches and structures the Opps queue for a given broker.
 * Filters to items where the broker is TM/Initial Contact or Advisor.
 * Excludes Discarded items. Returns { newLeads, inSequence, connected, closed }.
 */
export async function fetchOppsQueue(brokerName = 'Matt Mellott') {
  // Note: query_params filtering on status columns interferes with board_relation
  // linked_items resolution in Monday's API, so we filter Discarded client-side.
  const query = `
    query {
      boards(ids: [${OPPS_BOARD_ID}]) {
        items_page(limit: 300) {
          items {
            id
            name
            column_values {
              id
              type
              text
              value
              ... on BoardRelationValue {
                linked_items { id }
              }
            }
          }
        }
      }
    }
  `;

  const response = await mondayPost({ query });

  if (response.data?.errors?.length) {
    const msgs = response.data.errors.map((e) => e.message).join('; ');
    throw new Error(`Monday API error: ${msgs}`);
  }

  const board = response.data?.data?.boards?.[0];
  if (!board) throw new Error('Opps board data not returned from Monday.com');

  const allItems = board.items_page?.items ?? [];
  const brokerLower = brokerName.toLowerCase();

  const filtered = allItems.filter((item) => {
    // Must be assigned to this broker (TM/Initial Contact or Advisor)
    const tmText      = getColText(item, OPPS_COLS.tmContact).toLowerCase();
    const advisorText = getColText(item, OPPS_COLS.advisor).toLowerCase();
    if (!tmText.includes(brokerLower) && !advisorText.includes(brokerLower)) return false;

    // Exclude Discarded
    const stage = getColText(item, OPPS_COLS.pipelineStage);
    if (stage === 'Discarded') return false;

    return true;
  });

  // Group by stage category
  const newLeads   = [];
  const inSequence = [];
  const connected  = [];
  const closed     = [];

  filtered.forEach((item) => {
    const stage = getColText(item, OPPS_COLS.pipelineStage);
    const cfg   = OPPS_STAGE_CONFIG[stage];
    const group = cfg?.group ?? 'new';

    if (group === 'new')       newLeads.push(item);
    else if (group === 'sequence') inSequence.push(item);
    else if (group === 'connected') connected.push(item);
    else closed.push(item);
  });

  return { newLeads, inSequence, connected, closed };
}

/**
 * Fetches contact details from the Contacts board by item ID.
 * Returns { id, name, phone, email, company }
 */
export async function fetchContactDetails(contactItemId) {
  const query = `
    query {
      items(ids: [${contactItemId}]) {
        id
        name
        column_values(ids: [
          "phone", "email", "text7", "text85", "text",
          "dropdown9", "dropdown5", "dropdown8",
          "dropdown0__1", "dropdown_3__1", "dropdown_2__1", "dropdown_1__1"
        ]) {
          id
          text
          value
        }
      }
    }
  `;

  const response = await mondayPost({ query });

  if (response.data?.errors?.length) {
    const msgs = response.data.errors.map((e) => e.message).join('; ');
    throw new Error(`Monday API error: ${msgs}`);
  }

  const item = response.data?.data?.items?.[0];
  if (!item) throw new Error('Contact record not found in Monday.com');

  const get = (id) => item.column_values?.find((c) => c.id === id)?.text || '';

  return {
    id: item.id,
    name: item.name,
    phone: get('phone'),
    email: get('email'),
    company: get('text7'),
    title: get('text'),
    notes: get('text85'),
    leadSource: get('dropdown9'),
    advisorCreator: get('dropdown5'),
    contactType: get('dropdown8'),
    contentPreferences: get('dropdown0__1'),
    markets: get('dropdown_3__1'),
    persona: get('dropdown_2__1'),
    interests: get('dropdown_1__1'),
  };
}

/**
 * Updates contact details on the Contacts board.
 * Only writes fields that are provided (pass undefined to skip a field).
 */
export async function updateContactDetails(contactItemId, { name, phone, email, company, notes }) {
  const mutations = [];

  // Name requires its own mutation (item rename)
  if (name !== undefined) {
    mutations.push(`
      rename_item: change_column_value(
        board_id: ${CONTACTS_BOARD_ID},
        item_id: ${contactItemId},
        column_id: "name",
        value: ${JSON.stringify(JSON.stringify(name))}
      ) { id }
    `);
  }

  const columnValues = {};
  if (phone !== undefined) {
    // Monday phone column only accepts digits — strip all formatting (+, spaces, dashes, parens)
    const digitsOnly = phone.replace(/\D/g, '');
    columnValues['phone'] = { phone: digitsOnly.length > 0 ? digitsOnly : '', countryShortName: 'US' };
  }
  if (email !== undefined) columnValues['email'] = { email, text: email };
  if (company !== undefined) columnValues['text7'] = company;
  if (notes !== undefined) columnValues['text85'] = notes;

  if (Object.keys(columnValues).length > 0) {
    mutations.push(`
      update_cols: change_multiple_column_values(
        board_id: ${CONTACTS_BOARD_ID},
        item_id: ${contactItemId},
        column_values: ${JSON.stringify(JSON.stringify(columnValues))}
      ) { id }
    `);
  }

  if (mutations.length === 0) return;

  const mutation = `mutation { ${mutations.join('\n')} }`;

  const response = await mondayPost({ query: mutation }
  );

  if (response.data?.errors?.length) {
    const msgs = response.data.errors.map((e) => e.message).join('; ');
    throw new Error(`Monday API error: ${msgs}`);
  }
}

/**
 * Updates Priority and/or Prospect Type on the Prospecting board.
 */
export async function updateProspectDetails(prospectingItemId, { priority, prospectType }) {
  const columnValues = {};
  if (priority !== undefined) columnValues['priority_1'] = { label: priority };
  if (prospectType !== undefined) columnValues['status1'] = { label: prospectType };

  if (Object.keys(columnValues).length === 0) return;

  const mutation = `
    mutation {
      change_multiple_column_values(
        board_id: ${PROSPECTING_BOARD_ID},
        item_id: ${prospectingItemId},
        column_values: ${JSON.stringify(JSON.stringify(columnValues))}
      ) { id }
    }
  `;

  const response = await mondayPost({ query: mutation }
  );

  if (response.data?.errors?.length) {
    const msgs = response.data.errors.map((e) => e.message).join('; ');
    throw new Error(`Monday API error: ${msgs}`);
  }
}

/**
 * Fetches all Monday users and returns a name→id map.
 * Cached in memory for the session so we don't hit the API on every log entry.
 */
let _mondayUserCache = null;
async function getMondayUserIdByName(name) {
  if (!_mondayUserCache) {
    const res = await mondayPost({ query: `query { users { id name email } }` });
    const users = res.data?.data?.users ?? [];
    _mondayUserCache = {};
    for (const u of users) {
      _mondayUserCache[u.name] = u.id;
    }
  }
  return _mondayUserCache[name] || null;
}

/**
 * Writes a new entry to the Activity Log board.
 */
export async function createActivityLogEntry({
  contactItemId,
  prospectingItemName,
  activityType,
  outcome,
  notes,
  nextFollowUpDate,  // YYYY-MM-DD
  stageAtLog,
  brokerName,        // e.g. 'Joe Tredik'
}) {
  const today = todayStr();
  const itemName = `${activityType} — ${prospectingItemName} — ${today}`;

  // Step 1: create the item with all columns except long_text
  const columnValues = {
    date4: { date: today },
    dropdown_mm2dpctx: { labels: ['Prospecting'] },
    dropdown_mm2dv7tw: { labels: [activityType] },
    dropdown_mm2d1dzc: { labels: [outcome] },
  };
  // Only link to contact if we have a valid contact ID (null/undefined causes API error)
  if (contactItemId) {
    columnValues.board_relation_mm2dp4tg = { item_ids: [Number(contactItemId)] };
  }
  // Set the person (Broker) column using the Monday user ID
  if (brokerName) {
    try {
      const mondayUserId = await getMondayUserIdByName(brokerName);
      if (mondayUserId) {
        columnValues.person = { personsAndTeams: [{ id: Number(mondayUserId), kind: 'person' }] };
      }
    } catch { /* non-fatal — log entry still saves without person set */ }
  }
  // Next follow-up date is optional (Opps board doesn't use date-based scheduling)
  if (nextFollowUpDate) {
    columnValues.date_mm2d4sn = { date: nextFollowUpDate };
  }

  const createMutation = `
    mutation {
      create_item(
        board_id: ${ACTIVITY_LOG_BOARD_ID},
        item_name: ${JSON.stringify(itemName)},
        column_values: ${JSON.stringify(JSON.stringify(columnValues))}
      ) {
        id
      }
    }
  `;

  const createResponse = await mondayPost({ query: createMutation }
  );

  if (createResponse.data?.errors?.length) {
    const msgs = createResponse.data.errors.map((e) => e.message).join('; ');
    throw new Error(`Monday API error: ${msgs}`);
  }

  const newItemId = createResponse.data?.data?.create_item?.id;

  // Step 2: write notes separately via change_column_value (long_text requires this)
  if (newItemId && notes) {
    const notesMutation = `
      mutation {
        change_column_value(
          board_id: ${ACTIVITY_LOG_BOARD_ID},
          item_id: ${newItemId},
          column_id: "long_text_mm2dt8e3",
          value: ${JSON.stringify(JSON.stringify({ text: notes }))}
        ) {
          id
        }
      }
    `;
    await mondayPost({ query: notesMutation }
    );
  }

  return newItemId;
}

/**
 * Writes back to the Prospecting record after activity is logged.
 * Sets Last Contact = today, Next Follow Up = snooze date.
 */
export async function updateProspectingRecord(prospectingItemId, nextFollowUpDate) {
  const today = todayStr();

  const columnValues = {
    date1: { date: today },       // Last Contact
    date: { date: nextFollowUpDate }, // Next Follow Up
  };

  const mutation = `
    mutation {
      change_multiple_column_values(
        board_id: ${PROSPECTING_BOARD_ID},
        item_id: ${prospectingItemId},
        column_values: ${JSON.stringify(JSON.stringify(columnValues))}
      ) {
        id
      }
    }
  `;

  const response = await mondayPost({ query: mutation }
  );

  if (response.data?.errors?.length) {
    const msgs = response.data.errors.map((e) => e.message).join('; ');
    throw new Error(`Monday API error: ${msgs}`);
  }
}

/**
 * Returns the Monday of the current week (YYYY-MM-DD).
 */
function weekStartStr() {
  const d = new Date();
  const dayOfWeek = d.getDay(); // 0=Sun
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(d);
  monday.setDate(d.getDate() + mondayOffset);
  return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
}

/**
 * Returns the first day of the current month (YYYY-MM-DD).
 */
function monthStartStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * Fetches team scorecard data for the given period ('week' | 'month').
 * Returns { brokerStats, totals, startDate, period }
 *
 * brokerStats: [{ broker, calls, reached, showings, overdue, clearance }]
 * totals: { calls, reached, showings, overdue }
 *
 * NOTE: The Activity Log `person` column is only populated once broker auth
 * is wired up. Until then, all entries are attributed to Matt Mellott.
 */
export async function fetchScorecardData(period = 'week') {
  const today = todayStr();
  const startDate = period === 'week' ? weekStartStr() : monthStartStr();

  // Fetch Activity Log + Prospecting board in parallel
  const [activityRes, prospectRes] = await Promise.all([
    mondayPost({
      query: `
        query {
          boards(ids: [${ACTIVITY_LOG_BOARD_ID}]) {
            items_page(limit: 500) {
              items {
                id
                column_values(ids: ["date4", "person", "dropdown_mm2dv7tw", "dropdown_mm2d1dzc"]) {
                  id
                  text
                }
              }
            }
          }
        }
      `,
    }),
    mondayPost({
      query: `
        query {
          boards(ids: [${PROSPECTING_BOARD_ID}]) {
            items_page(limit: 500) {
              items {
                id
                column_values(ids: ["person", "date", "priority_1", "status1"]) {
                  id
                  text
                }
              }
            }
          }
        }
      `,
    }),
  ]);

  // ── Activity Log aggregation ───────────────────────────────────────────────
  const activityItems = activityRes.data?.data?.boards?.[0]?.items_page?.items ?? [];

  // Filter to the selected period, exclude Discard entries
  const periodEntries = activityItems.filter((item) => {
    const get = (id) => item.column_values?.find((c) => c.id === id)?.text || '';
    const date = get('date4');
    const actType = get('dropdown_mm2dv7tw');
    return date >= startDate && date <= today && actType !== 'Discard';
  });

  // Group stats by broker name (person column text)
  // Phase 1: empty person → default to Matt Mellott
  const brokerMap = {};
  const ensureBroker = (name) => {
    const key = name || 'Matt Mellott';
    if (!brokerMap[key]) brokerMap[key] = { calls: 0, reached: 0, showings: 0 };
    return key;
  };

  periodEntries.forEach((item) => {
    const get = (id) => item.column_values?.find((c) => c.id === id)?.text || '';
    const key = ensureBroker(get('person'));
    const actType = get('dropdown_mm2dv7tw');
    const outcome = get('dropdown_mm2d1dzc');

    brokerMap[key].calls++;
    if (outcome === 'Reached') brokerMap[key].reached++;
    if (actType === 'Showing') brokerMap[key].showings++;
  });

  // ── Prospecting board — overdue per broker ─────────────────────────────────
  const ACTIVE_TYPES      = ['int requirement', 'ext requirement', 'off market / pocket', 'off market/pocket'];
  const ACTIVE_PRIORITIES = ['(1) urgent', '(2) motivated', '(3) active'];

  const prospectItems = prospectRes.data?.data?.boards?.[0]?.items_page?.items ?? [];
  const overdueCounts = {};

  prospectItems.forEach((item) => {
    const get = (id) => item.column_values?.find((c) => c.id === id)?.text || '';
    const broker = get('person') || 'Matt Mellott';
    const followUp = get('date');
    const priority = get('priority_1').toLowerCase().trim();
    const prospectType = get('status1').toLowerCase().trim();

    if (!ACTIVE_PRIORITIES.includes(priority)) return;
    if (!ACTIVE_TYPES.some((t) => prospectType.includes(t))) return;
    if (!followUp || followUp >= today) return;

    overdueCounts[broker] = (overdueCounts[broker] || 0) + 1;
  });

  // ── Merge into broker rows ─────────────────────────────────────────────────
  const allBrokers = new Set([...Object.keys(brokerMap), ...Object.keys(overdueCounts)]);

  const brokerStats = Array.from(allBrokers).map((broker) => {
    const acts = brokerMap[broker] || { calls: 0, reached: 0, showings: 0 };
    const overdue = overdueCounts[broker] || 0;
    const clearance = (acts.calls + overdue) > 0
      ? Math.round((acts.calls / (acts.calls + overdue)) * 100)
      : 0;
    return { broker, ...acts, overdue, clearance };
  });

  // Sort by calls logged descending
  brokerStats.sort((a, b) => b.calls - a.calls || a.broker.localeCompare(b.broker));

  // ── Team totals ────────────────────────────────────────────────────────────
  const totals = brokerStats.reduce(
    (acc, b) => ({
      calls: acc.calls + b.calls,
      reached: acc.reached + b.reached,
      showings: acc.showings + b.showings,
      overdue: acc.overdue + b.overdue,
    }),
    { calls: 0, reached: 0, showings: 0, overdue: 0 }
  );

  return { brokerStats, totals, startDate, period };
}

// Discard reasons that also trigger a Do Not Contact flag on the Contacts board
const DISCARD_DNC_REASONS = ['Hostile—DNC'];

// Contacts board DNC column IDs (dropdown columns)
// no_call values:  'Call' | 'Do Not Call'
// no_email values: 'Email' | 'Do Not Email' | 'Client' | 'TBD'
// no_mail values:  'Mail' | 'Do Not Mail' | 'Client' | 'Ask Advisors' | 'Deceased'

/**
 * Discards a prospect:
 * 1. Logs an Activity Log entry (type=Discard, outcome=reason)
 * 2. Sets Prospect Type to "Lost" on the Prospecting board
 * 3. If Hostile—DNC: sets Do Not Contact flag on the Contacts board
 *
 * NOTE: "Discard" must exist as an option in the Activity Type dropdown
 * on the Activity Log board, and each reason must exist in the Outcome dropdown.
 * Add these in Monday.com board settings → Column settings → Dropdown options.
 */
export async function discardProspect(prospectingItemId, contactItemId, reason) {
  const today = todayStr();
  const itemName = `Discard — ${reason} — ${today}`;

  // Step 1: Create Activity Log entry
  const columnValues = {
    date4: { date: today },
    dropdown_mm2dpctx: { labels: ['Prospecting'] },
    dropdown_mm2dv7tw: { labels: ['Discard'] },
    dropdown_mm2d1dzc: { labels: [reason] },
  };
  if (contactItemId) {
    columnValues.board_relation_mm2dp4tg = { item_ids: [Number(contactItemId)] };
  }

  const createMutation = `
    mutation {
      create_item(
        board_id: ${ACTIVITY_LOG_BOARD_ID},
        item_name: ${JSON.stringify(itemName)},
        column_values: ${JSON.stringify(JSON.stringify(columnValues))}
      ) { id }
    }
  `;

  const createRes = await mondayPost({ query: createMutation });
  if (createRes.data?.errors?.length) {
    const msgs = createRes.data.errors.map((e) => e.message).join('; ');
    throw new Error(`Activity Log error: ${msgs}`);
  }

  // Step 2: Set Prospect Type to "Lost" on Prospecting board
  const lostMutation = `
    mutation {
      change_multiple_column_values(
        board_id: ${PROSPECTING_BOARD_ID},
        item_id: ${prospectingItemId},
        column_values: ${JSON.stringify(JSON.stringify({ status1: { label: 'Lost' } }))}
      ) { id }
    }
  `;

  const lostRes = await mondayPost({ query: lostMutation });
  if (lostRes.data?.errors?.length) {
    const msgs = lostRes.data.errors.map((e) => e.message).join('; ');
    throw new Error(`Prospect status error: ${msgs}`);
  }

  // Step 3: If Hostile—DNC, flag all three contact columns on the Contacts board
  if (DISCARD_DNC_REASONS.includes(reason) && contactItemId) {
    const dncValues = {
      no_call:  { label: 'Do Not Call' },
      no_email: { label: 'Do Not Email' },
      no_mail:  { label: 'Do Not Mail' },
    };
    const dncMutation = `
      mutation {
        change_multiple_column_values(
          board_id: ${CONTACTS_BOARD_ID},
          item_id: ${contactItemId},
          column_values: ${JSON.stringify(JSON.stringify(dncValues))}
        ) { id }
      }
    `;
    // Fire-and-forget — discard still completes even if this fails
    mondayPost({ query: dncMutation }).catch((err) => {
      console.warn('DNC flags not set on Contacts record:', err.message);
    });
  }
}

/**
 * Fetches all subitems (Requirements) for a given Prospecting board item.
 * Returns the raw subitem array — each item has { id, name, column_values }.
 */
export async function fetchSubitems(prospectItemId) {
  const query = `
    query {
      items(ids: [${prospectItemId}]) {
        subitems {
          id
          name
          column_values {
            id
            text
            value
          }
        }
      }
    }
  `;

  const response = await mondayPost({ query }
  );

  if (response.data?.errors?.length) {
    const msgs = response.data.errors.map((e) => e.message).join('; ');
    throw new Error(`Monday API error: ${msgs}`);
  }

  return response.data?.data?.items?.[0]?.subitems ?? [];
}

/**
 * Creates a new subitem (Requirement) under a Prospecting board item.
 * Column IDs match the Requirements subitem board on SterlingCRE.
 *
 * @param {string}   prospectItemId  Parent item ID on the Prospecting board
 * @param {object}   fields
 *   name           {string}   — auto-generated if omitted
 *   status         {string}   — status_1 label (default: "Looking")
 *   assignmentType {string}   — dropdown  (single label)
 *   assetTypes     {string[]} — dropdown2 (multi-label)
 *   sfMin          {number}   — numbers
 *   sfMax          {number}   — numbers1
 *   acresMin       {number}   — numbers4
 *   acresMax       {number}   — numbers5
 *   priceMin       {number}   — numbers3
 *   priceMax       {number}   — numbers59
 *   location       {string}   — dropdown6 (single label)
 *   notes          {string}   — text
 *
 * Returns the newly created subitem object { id, name, column_values }.
 */
export async function createSubitem(prospectItemId, fields) {
  const {
    name,
    status = 'Looking',
    assignmentType,
    assetTypes = [],
    sfMin, sfMax,
    acresMin, acresMax,
    priceMin, priceMax,
    location,
    notes,
  } = fields;

  const columnValues = {};
  if (status)                                  columnValues['status_1']  = { label: status };
  if (assignmentType)                          columnValues['dropdown']   = { labels: [assignmentType] };
  if (assetTypes?.length)                      columnValues['dropdown2']  = { labels: assetTypes };
  if (sfMin    != null && sfMin    !== '')      columnValues['numbers4']   = parseFloat(sfMin);
  if (sfMax    != null && sfMax    !== '')      columnValues['numbers5']   = parseFloat(sfMax);
  if (acresMin != null && acresMin !== '')      columnValues['numbers3']   = parseFloat(acresMin);
  if (acresMax != null && acresMax !== '')      columnValues['numbers59']  = parseFloat(acresMax);
  if (priceMin != null && priceMin !== '')      columnValues['numbers']    = parseFloat(priceMin);
  if (priceMax != null && priceMax !== '')      columnValues['numbers1']   = parseFloat(priceMax);
  if (location)                                columnValues['dropdown6']  = { labels: [location] };
  if (notes)                                   columnValues['text']       = notes;

  const mutation = `
    mutation {
      create_subitem(
        parent_item_id: ${prospectItemId},
        item_name: ${JSON.stringify(name || 'New Requirement')},
        column_values: ${JSON.stringify(JSON.stringify(columnValues))}
      ) {
        id
        name
        column_values {
          id
          text
          value
        }
      }
    }
  `;

  const response = await mondayPost({ query: mutation }
  );

  if (response.data?.errors?.length) {
    const msgs = response.data.errors.map((e) => e.message).join('; ');
    throw new Error(`Monday API error: ${msgs}`);
  }

  return response.data?.data?.create_subitem;
}

/**
 * Searches contacts on the Contacts board by name (case-insensitive partial match).
 * Returns array of { id, name, column_values }
 */
export async function searchContacts(query) {
  const q = `
    query {
      boards(ids: [${CONTACTS_BOARD_ID}]) {
        items_page(limit: 20, query_params: {
          rules: [{
            column_id: "name",
            compare_value: [${JSON.stringify(query)}],
            operator: contains_text
          }]
        }) {
          items {
            id
            name
            column_values(ids: ["phone", "email", "text7"]) {
              id
              text
            }
          }
        }
      }
    }
  `;
  const response = await mondayPost({ query: q });
  if (response.data?.errors?.length) {
    throw new Error(response.data.errors[0].message);
  }
  return response.data?.data?.boards?.[0]?.items_page?.items ?? [];
}

/**
 * Creates a new contact on the Contacts board.
 * Returns { id, name }
 */
export async function createContact({
  name, phone, email, company, title,
  leadSource, advisorCreator, contactType, persona,
  interests = [], markets = [], contentPreferences = [],
  notes,
}) {
  const columnValues = {};
  if (phone)   columnValues['phone']  = { phone: phone.replace(/\D/g, ''), countryShortName: 'US' };
  if (email)   columnValues['email']  = { email, text: email };
  if (company) columnValues['text7']  = company;
  if (title)   columnValues['text']   = title;
  if (notes)   columnValues['text85'] = notes;

  // Single-select dropdowns
  if (leadSource)      columnValues['dropdown9']     = { labels: [leadSource] };
  if (advisorCreator)  columnValues['dropdown5']     = { labels: [advisorCreator] };
  if (contactType)     columnValues['dropdown8']     = { labels: [contactType] };
  if (persona?.length) columnValues['dropdown_2__1'] = { labels: Array.isArray(persona) ? persona : [persona] };

  // Multi-select dropdowns
  if (interests.length)          columnValues['dropdown_1__1'] = { labels: interests };
  if (markets.length)            columnValues['dropdown_3__1'] = { labels: markets };
  if (contentPreferences.length) columnValues['dropdown0__1']   = { labels: contentPreferences };
  if (contentPreferences.length) columnValues['color_mkq01p5f'] = { label: 'Send to Brevo' };

  const mutation = `
    mutation {
      create_item(
        board_id: ${CONTACTS_BOARD_ID},
        item_name: ${JSON.stringify(name)},
        column_values: ${JSON.stringify(JSON.stringify(columnValues))}
      ) { id name }
    }
  `;
  const response = await mondayPost({ query: mutation });
  if (response.data?.errors?.length) {
    throw new Error(response.data.errors[0].message);
  }
  return response.data?.data?.create_item;
}

/**
 * Fetches all Monday.com users in the account.
 * Returns [{ id, name }]
 */
export async function fetchMondayUsers() {
  const q = `query { users { id name } }`;
  const response = await mondayPost({ query: q });
  if (response.data?.errors?.length) throw new Error(response.data.errors[0].message);
  return response.data?.data?.users ?? [];
}

/**
 * Reassigns a Prospecting record to a different broker.
 * Updates the person (Main PoC) column and posts a handoff note on the record.
 */
export async function reassignProspect(prospectingItemId, newBrokerName, fromBrokerName) {
  const users = await fetchMondayUsers();
  const target = users.find((u) => u.name.toLowerCase() === newBrokerName.toLowerCase());
  if (!target) throw new Error(`Could not find Monday user: ${newBrokerName}`);

  const columnValues = {
    person: { personsAndTeams: [{ id: Number(target.id), kind: 'person' }] },
  };

  const mutation = `
    mutation {
      change_multiple_column_values(
        board_id: ${PROSPECTING_BOARD_ID},
        item_id: ${prospectingItemId},
        column_values: ${JSON.stringify(JSON.stringify(columnValues))}
      ) { id }
    }
  `;

  const response = await mondayPost({ query: mutation });
  if (response.data?.errors?.length) throw new Error(response.data.errors[0].message);

  // Post a handoff note on the Prospecting record for tracking — fire and forget
  const today = todayStr();
  const noteText = `Sent to ${newBrokerName}${fromBrokerName ? ` (from ${fromBrokerName})` : ''} — ${today}`;
  const noteMutation = `mutation { create_update(item_id: ${prospectingItemId}, body: ${JSON.stringify(noteText)}) { id } }`;
  mondayPost({ query: noteMutation }).catch((err) => console.warn('Handoff note failed:', err.message));
}

/**
 * Creates a new prospecting record on the Prospecting board linked to a contact.
 * Pass assignedBrokerName to route the record to a specific teammate on creation.
 * Returns { id }
 */
export async function createProspectRecord(contactItemId, contactName, { prospectType, priority, nextFollowUp, assignedBrokerName }) {
  const columnValues = {};
  if (prospectType)  columnValues['status1']       = { label: prospectType };
  if (priority)      columnValues['priority_1']     = { label: priority };
  if (nextFollowUp)  columnValues['date']            = { date: nextFollowUp };
  if (contactItemId) columnValues['connect_boards0'] = { item_ids: [Number(contactItemId)] };

  // Assign to a specific broker if provided (requires Monday user ID lookup)
  if (assignedBrokerName) {
    const users = await fetchMondayUsers();
    const target = users.find((u) => u.name.toLowerCase() === assignedBrokerName.toLowerCase());
    if (target) columnValues['person'] = { personsAndTeams: [{ id: Number(target.id), kind: 'person' }] };
  }

  const mutation = `
    mutation {
      create_item(
        board_id: ${PROSPECTING_BOARD_ID},
        item_name: ${JSON.stringify(contactName)},
        column_values: ${JSON.stringify(JSON.stringify(columnValues))}
      ) { id }
    }
  `;
  const response = await mondayPost({ query: mutation });
  if (response.data?.errors?.length) {
    throw new Error(response.data.errors[0].message);
  }
  return response.data?.data?.create_item;
}

/**
 * Strips HTML tags and decodes basic entities from Monday update bodies.
 */
function stripHtml(html) {
  return (html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim();
}

/**
 * Fetches Updates section comments from a Contacts board item.
 * Returns array of { date, author, text, source: 'update' }
 */
export async function fetchContactUpdates(contactItemId) {
  const query = `
    query {
      items(ids: [${contactItemId}]) {
        updates(limit: 20) {
          id
          body
          created_at
          creator { name }
        }
      }
    }
  `;

  const response = await mondayPost({ query }
  );

  if (response.data?.errors?.length) {
    const msgs = response.data.errors.map((e) => e.message).join('; ');
    throw new Error(`Monday API error: ${msgs}`);
  }

  const updates = response.data?.data?.items?.[0]?.updates ?? [];

  return updates
    .map((u) => ({
      date: u.created_at ? u.created_at.slice(0, 10) : '',
      author: u.creator?.name || '',
      text: stripHtml(u.body),
      source: 'update',
    }))
    .filter((u) => u.text.length > 0);
}

/**
 * Fetches the last 5 activity log entries for a given contact item ID.
 * Returns array of { date, activityType, outcome, notes }
 */
export async function fetchCallHistory(contactItemId) {
  if (!contactItemId) return [];

  // Fetch recent activity log entries and filter client-side.
  // We avoid query_params on board_relation columns — Monday's API doesn't reliably
  // return linked_items when query_params is active, and the any_of filter has had
  // inconsistent behaviour. Client-side matching against the value JSON is more reliable.
  const query = `
    query {
      boards(ids: [${ACTIVITY_LOG_BOARD_ID}]) {
        items_page(limit: 100) {
          items {
            id
            name
            column_values(ids: ["board_relation_mm2dp4tg", "date4", "dropdown_mm2dv7tw", "dropdown_mm2d1dzc", "long_text_mm2dt8e3", "person"]) {
              id
              text
              value
              ... on BoardRelationValue { linked_items { id } }
            }
          }
        }
      }
    }
  `;

  const response = await mondayPost({ query }
  );

  if (response.data?.errors?.length) {
    const msgs = response.data.errors.map((e) => e.message).join('; ');
    throw new Error(`Monday API error: ${msgs}`);
  }

  const allItems = response.data?.data?.boards?.[0]?.items_page?.items ?? [];

  // Filter client-side using linked_items (board_relation value field is always null;
  // linked_items inline fragment is the only reliable way to read relation links).
  const targetId = String(contactItemId);
  const matched = allItems.filter((item) => {
    const relCol = item.column_values?.find((c) => c.id === 'board_relation_mm2dp4tg');
    return relCol?.linked_items?.some((li) => String(li.id) === targetId);
  });

  // Sort by date descending, take last 5
  matched.sort((a, b) => {
    const da = a.column_values?.find((c) => c.id === 'date4')?.text || '';
    const db = b.column_values?.find((c) => c.id === 'date4')?.text || '';
    return db.localeCompare(da);
  });

  return matched.slice(0, 5).map((item) => {
    const get = (id) => item.column_values?.find((c) => c.id === id)?.text || '';
    return {
      date: get('date4'),
      activityType: get('dropdown_mm2dv7tw'),
      outcome: get('dropdown_mm2d1dzc'),
      notes: get('long_text_mm2dt8e3'),
      broker: get('person'),
    };
  });
}
